/**
 * Absolute dates and times in the reader's locale: „23.09.2026“,
 * „23. Sept. 2026“, „14:05“, „September 2026“. For „vor 5 Minuten“ use
 * `formatRelativeTime` (RelativeTime).
 *
 * Every function takes a `Date`, an ISO string or an epoch number, and gives
 * `''` (or `fallback`) for anything it cannot read. A date-only ISO string
 * (`2026-09-23`) is read as LOCAL midnight — `new Date('2026-09-23')` would be
 * UTC midnight and show the day before west of Greenwich.
 */
import type { NxDateLike } from '../RelativeTime/relativeTime';
import { dateTimeFormat } from './intlCache';

/**
 * - `short` „23.09.2026“
 * - `medium` „23. Sept. 2026“
 * - `long` „23. September 2026“
 * - `full` „Mittwoch, 23. September 2026“
 */
export type NxDateStyle = 'short' | 'medium' | 'long' | 'full';

/** What every date formatter takes. */
export interface NxFormatDateBaseOptions {
  /** BCP-47 tag; the runtime default when left out. */
  locale?: string;
  /** IANA time zone, e.g. `Europe/Berlin`; the runtime's zone when left out. */
  timeZone?: string;
  /** Shown for empty or unreadable input. Defaults to `''`. */
  fallback?: string;
}

export interface NxFormatDateOptions extends NxFormatDateBaseOptions {
  style?: NxDateStyle;
}

export interface NxFormatDateTimeOptions extends NxFormatDateOptions {
  /** Include seconds in the time part. */
  seconds?: boolean;
  /**
   * Put this between the date and the time (e.g. `' · '`). Left out, the
   * locale joins them itself („23.09.2026, 14:05“, „23. September 2026 um 14:05“).
   */
  separator?: string;
}

export interface NxFormatTimeOptions extends NxFormatDateBaseOptions {
  seconds?: boolean;
}

export interface NxFormatMonthOptions extends NxFormatDateBaseOptions {
  /** `short` „Sept. 2026“, `long` „September 2026“ (default). */
  style?: 'short' | 'long';
}

const DATE_STYLES: Record<NxDateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
};

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The value as a valid `Date`, or `null`. Date-only ISO strings become local
 * midnight, and impossible dates like `2026-02-30` are rejected instead of
 * rolling over into March.
 */
export function parseDateLike(value: NxDateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const m = ISO_DATE_ONLY.exec(value.trim());
    if (m) {
      const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
      const date = new Date(y, mo - 1, d);
      return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
        ? date
        : null;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function withZone(
  options: Intl.DateTimeFormatOptions,
  timeZone: string | undefined
): Intl.DateTimeFormatOptions {
  return timeZone ? { ...options, timeZone } : options;
}

function timeOptions(seconds: boolean | undefined): Intl.DateTimeFormatOptions {
  return seconds
    ? { hour: '2-digit', minute: '2-digit', second: '2-digit' }
    : { hour: '2-digit', minute: '2-digit' };
}

function resolve<T extends { style?: string }>(styleOrOptions: T['style'] | T | undefined): T {
  if (typeof styleOrOptions === 'string') return { style: styleOrOptions } as T;
  return (styleOrOptions ?? {}) as T;
}

/**
 * The date alone. `formatDate(value, 'medium')` is short for
 * `formatDate(value, { style: 'medium' })`; the default style is `short`.
 */
export function formatDate(
  value: NxDateLike,
  styleOrOptions?: NxDateStyle | NxFormatDateOptions
): string {
  const options = resolve<NxFormatDateOptions>(styleOrOptions);
  const date = parseDateLike(value);
  if (!date) return options.fallback ?? '';
  const intl = withZone(DATE_STYLES[options.style ?? 'short'], options.timeZone);
  return dateTimeFormat(options.locale, intl).format(date);
}

/** Date and time: „23.09.2026, 14:05“; the `style` shapes the date part. */
export function formatDateTime(
  value: NxDateLike,
  styleOrOptions?: NxDateStyle | NxFormatDateTimeOptions
): string {
  const options = resolve<NxFormatDateTimeOptions>(styleOrOptions);
  const date = parseDateLike(value);
  if (!date) return options.fallback ?? '';
  const dateOptions = DATE_STYLES[options.style ?? 'short'];
  const timeOpts = timeOptions(options.seconds);
  if (options.separator !== undefined) {
    const datePart = dateTimeFormat(options.locale, withZone(dateOptions, options.timeZone));
    const timePart = dateTimeFormat(options.locale, withZone(timeOpts, options.timeZone));
    return datePart.format(date) + options.separator + timePart.format(date);
  }
  const intl = withZone({ ...dateOptions, ...timeOpts }, options.timeZone);
  return dateTimeFormat(options.locale, intl).format(date);
}

/** The time of day: „14:05“, or „14:05:09“ with `seconds`. */
export function formatTime(value: NxDateLike, options: NxFormatTimeOptions = {}): string {
  const date = parseDateLike(value);
  if (!date) return options.fallback ?? '';
  const intl = withZone(timeOptions(options.seconds), options.timeZone);
  return dateTimeFormat(options.locale, intl).format(date);
}

/** Month and year for period headings: „September 2026“ or „Sept. 2026“. */
export function formatMonth(
  value: NxDateLike,
  styleOrOptions?: 'short' | 'long' | NxFormatMonthOptions
): string {
  const options = resolve<NxFormatMonthOptions>(styleOrOptions);
  const date = parseDateLike(value);
  if (!date) return options.fallback ?? '';
  const intl = withZone(
    { month: options.style === 'short' ? 'short' : 'long', year: 'numeric' },
    options.timeZone
  );
  return dateTimeFormat(options.locale, intl).format(date);
}

/** Day and month without the year, for dense lists: „23.09.“. */
export function formatDayMonth(value: NxDateLike, options: NxFormatDateBaseOptions = {}): string {
  const date = parseDateLike(value);
  if (!date) return options.fallback ?? '';
  const intl = withZone({ day: '2-digit', month: '2-digit' }, options.timeZone);
  return dateTimeFormat(options.locale, intl).format(date);
}
